import 'project/polyfill'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import { I18NConfig } from 'next/dist/server/config-shared'
import { createClient } from '../prismicio'
import { getPosts, getReviews } from 'common/cms-sdk'
import { getAddons, getPlans } from 'common/planData'
import { Utils } from 'common/utils'
import { useRouter } from 'next/router'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'

import HeroBanner from 'components/hero/HeroBanner'
import {
  TAddon,
  TBlogPostResult,
  TPlanSummary,
  TReviews,
} from 'common/cms-types'

import { Query } from '@prismicio/types'
import { seoStrings } from 'common/strings/seo-strings'
import ReviewsSection from 'components/sections/ReviewsSection'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import HeadingAndText from 'components/HeadingAndText'
import BlogSection from 'components/sections/BlogSection'
import PartnersCard from 'components/cards/new/PartnersCard'
import FeaturesCard from 'components/cards/new/FeaturesCard'
import AppPromoCard from 'components/cards/new/AppPromoCard'
import PartnersTestimonial from 'components/cards/new/PartnerTestimonialCard'
import VirtualAddressPromoCard from 'components/cards/new/VirtualAddressPromoCard'
import VirtualOfficePromoCard from 'components/cards/new/VirtualOfficePromoCard'
import SaaSOfficeSolutionCard from 'components/cards/new/SaaSOfficeSolutionCard'
import classNames from 'classnames'
import {
  heroGap,
  marginHeader,
  sectionGap,
  sectionGapSmall,
  sectionPadding,
  sectionPaddingLarge,
} from 'project/padding'
import MailPortalHighlights, {
  saasSolutionsHighlights,
} from 'components/cards/new/common/MailPortalHighlights'
import ContactUsCard from 'components/cards/new/ContactUsCard'
import TidePromoCard from 'components/cards/new/TidePromoCard'
import SubscriptionPricing from 'components/SubscriptionPricing'
import SalesPitchCard from 'components/cards/new/common/SalesPitchCard'
import WhoAreWeCard from 'components/cards/new/WhoAreWeCard'
import InnovationRecognitionSummary from 'components/cards/new/InnovationRecognitionSummary'
import EmbededNewsletterForm from 'components/sections/EmbededNewsletterForm'
import { PlanSlug } from 'common/planSlugs'
import Link from 'next/link'
import { HoxtonMixLogo } from 'components/icons'
import Button from 'components/forms/Button'

export type HomePageType = {
  posts: Query<TBlogPostResult>
  plans: TPlanSummary[]
  reviews: TReviews
  addons: TAddon[]
}

const HomePage: NextPageWithLayout<HomePageType> = ({
  addons,
  plans,
  posts,
  reviews,
}) => {
  const router = useRouter()
  const seoProps: SeoContainerProps['seoProps'] = {
    description: seoStrings.indexDescription,
    openGraph: {
      description: seoStrings.indexOGDescription,
      url: 'https://www.hoxtonmix.com/',
    },
    title: seoStrings.indexTitle,
  }
  const planSummary = plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!
  return (
    <SeoContainer
      alternate={[
        { href: '/au', lang: 'en-au' },
        { href: '/', lang: 'en-gb' },
      ]}
      breadcrumb={homeBreadcrumb()}
      seoProps={seoProps}
    >
      <main
        className={classNames(
          'overflow-x-hidden new-design d-flex flex-column bg-grey6',
          sectionGap,
        )}
      >
        <div className={classNames('d-flex flex-column', heroGap)}>
          <HeroBanner
            logo={
              <HoxtonMixLogo
                className='mb-5 mb-lg-8'
                containerClassName='w-lg-55'
              />
            }
            robotImage={{
              alt: 'HaiRo™ robot mascot displaying mail interface',
              dimensions: {
                height: 1000,
                width: 827,
              },
              src: '/images/new/robot-mail-preview.png',
            }}
            cta={
              <SubscriptionPricing
                hideRegister
                showLink
                planSummary={planSummary}
                rightButton={
                  <Link
                    aria-label='Take a Product Tour'
                    className='text-decoration-none'
                    href={'/virtual-office-london-old-street#demo'}
                  >
                    <Button
                      theme={'secondary'}
                      className={classNames(
                        'w-100 text-decoration-none d-none d-xl-block px-12 w-lg-auto mt-5 mt-lg-8',
                        {},
                      )}
                    >
                      Take a Product Tour
                    </Button>
                  </Link>
                }
              />
            }
            title='Want an Impressive Virtual Office Address?'
            description={
              <div className={classNames('mb-2')}>
                <div className='fs-fluid-24-32 fw-semibold'>
                  Register your London business address and let our AI agent{' '}
                  <span className='fw-bold color-pink2'>HaiRo™</span> organise,
                  digitise, and deliver your post.
                </div>
              </div>
            }
            subtitle={<></>}
          />
          <div
            className={classNames('container d-flex flex-column', sectionGap)}
          >
            <h2
              className={classNames(
                'fs-fluid-36-80 lh-105 fw-semibold text-center',
                sectionPadding,
              )}
            >
              Our
              <span className='color-pink2'> AI-powered </span>
              mail processing agent, HaiRo™, scans your post and delivers it
              directly to your inbox.
            </h2>
          </div>
        </div>

        <div
          className={classNames(
            sectionGapSmall,
            'container d-flex flex-column mt-4',
          )}
        >
          <VirtualOfficePromoCard />
          <PartnersCard />
        </div>
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          <div>
            <HeadingAndText
              className={classNames(
                'px-lg-0 px-6 ',
                marginHeader,
                sectionPadding,
              )}
              classesForTitle='fs-fluid-36-80 lh-105'
              subtitleSize='large'
              title={`Our virtual office service includes everything in <span class="color-pink2">one simple plan</span>.`}
              subtitle={`<h3 class="fw-semibold">Let HaiRo™ take care of your mail and documents.</h3>`}
            />
            <FeaturesCard planSummary={planSummary} />
          </div>
          <div>
            <h2
              className={classNames(
                'fs-fluid-36-80 lh-105 fw-semibold text-center',
                marginHeader,
                sectionPaddingLarge,
              )}
            >
              Thousands of companies have trusted us with over
              <br />
              <span className='color-pink2'>1 million pieces of mail</span>.
            </h2>
            <div className={marginHeader}>
              <PartnersTestimonial />
            </div>
            <div className={'text-center'}>
              <SubscriptionPricing planSummary={planSummary} />
            </div>
          </div>
          <div
            className={classNames(
              sectionGapSmall,
              'container d-flex flex-column mt-4',
            )}
          >
            <AppPromoCard planSummary={planSummary} />
            <VirtualAddressPromoCard
              addons={addons}
              planSummary={planSummary}
            />
          </div>

          <SalesPitchCard
            plans={plans}
            headerSize={2}
            subscriptionPricingClassName={'me-xl-20'}
            pitchText='No setup or handling fees — not even hidden ones.'
          />
          <div
            className={classNames(
              sectionGapSmall,
              'container d-flex flex-column mt-4',
            )}
          >
            <div className='row gy-5'>
              <div className='col-12 col-lg-6 '>
                <ContactUsCard />
              </div>
              <div className='col-12 col-lg-6'>
                <TidePromoCard />
              </div>
            </div>
            <SaaSOfficeSolutionCard />
          </div>
          <MailPortalHighlights
            headingSize={4}
            title='SaaS solutions for accountancies and fintech.'
          >
            {saasSolutionsHighlights.map((highlight, index) => (
              <div className='pb-4' key={index}>
                <span className='fw-semibold'>{highlight.title}</span>
                <p className='pt-4'>{highlight.content}</p>
              </div>
            ))}
            <div className='text-center'>
              <Link
                className='btn btn-lg btn-primary w-100 w-lg-auto mt-2 px-22 mt-lg-13'
                href={'/enterprise'}
              >
                Enterprise Solutions
              </Link>
            </div>
          </MailPortalHighlights>
        </div>
        <BlogSection posts={posts} />
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          <WhoAreWeCard />
          <InnovationRecognitionSummary />
          <EmbededNewsletterForm />
        </div>

        <ReviewsSection data={reviews} pageUrl={router.pathname} />
      </main>
    </SeoContainer>
  )
}
const PAGE_SIZE = 4

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    const plans = await getPlans()
    const posts = await getPosts(client, 1, lang, PAGE_SIZE, undefined)
    const addons = await getAddons(true)
    const reviews = await getReviews()
    return {
      props: {
        addons,
        plans,
        posts,
        reviews,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}

export default HomePage
